import { Link } from "react-router-dom";
import { BookOpen, Gamepad2, Laptop, Cpu } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageShell, GuideHero } from "@/components/layout/PageShell";

const FAQ = [
  {
    id: "direct",
    q: "Can an Xbox controller connect straight to a SPIKE Prime hub?",
    a: (
      <>
        <p><b>Yes — but not with LEGO's own firmware.</b> The hub's Bluetooth chip is capable of it, but LEGO's firmware only lets the hub be connected <i>to</i> (by the SPIKE app). It never goes looking for a controller.</p>
        <p>The free Pybricks firmware adds the missing part: the hub scans for the controller, connects, pairs, remembers it, and reads its sticks and buttons. Once your robot setup is on the hub, you can close the laptop.</p>
      </>
    ),
  },
  {
    id: "challenge",
    q: "Is that hard to do?",
    a: (
      <ul className="list-disc space-y-1.5 pl-5">
        <li>The hub has to be two things at once: something a laptop connects to <i>and</i> something that connects out to a controller.</li>
        <li>Xbox controllers only speak Bluetooth Low Energy after a controller update (from 2021).</li>
        <li>Pairing keys must be saved so it reconnects later, and the controller's data format must be decoded.</li>
        <li>It must be safe when the signal drops. HubPilot adds a failsafe to every robot setup: motors stop at once, then it reconnects by itself.</li>
      </ul>
    ),
  },
  {
    id: "pybricks",
    q: "How does Pybricks do it?",
    a: (
      <>
        <p>Pybricks replaces the hub firmware with MicroPython and its own Bluetooth stack. Since v3.3 it includes an <code>XboxController</code> class that does all of the above. The firmware is free to install from code.pybricks.com. Much of Pybricks is open source under the MIT licence; some parts, including Xbox controller support, are not.</p>
        <p>With Pybricks you write the program yourself. HubPilot asks how your robot is built and how you want to drive, makes the driving program for you and sends it to a hub running the official Pybricks firmware. HubPilot is independent and isn't made or endorsed by Pybricks. If it helps you, please <a className="font-semibold text-primary underline" href="https://www.patreon.com/pybricks" target="_blank" rel="noreferrer" data-testid="faq-support-pybricks-link">support the Pybricks team</a>.</p>
      </>
    ),
  },
  {
    id: "many",
    q: "Can we have dozens or hundreds of pairings in one place?",
    a: (
      <>
        <p><b>Dozens: yes, easily.</b> Each Bluetooth link hops across 37 radio channels and uses very little of the airwaves. A class of 30 robots is fine.</p>
        <p><b>Hundreds: yes, with routine.</b> A competition hall gets crowded, so pair one controller at a time at a pairing station, keep each controller with its hub, switch off spare controllers and disconnect laptops after downloading.</p>
        <p>The real risk isn't the radio, it's a hub grabbing the wrong controller. <Link className="font-semibold text-primary underline" to="/classroom">See the classroom pairing guide.</Link></p>
      </>
    ),
  },
  {
    id: "updates",
    q: "Are firmware updates necessary? How are they handled?",
    a: (
      <ul className="list-disc space-y-1.5 pl-5">
        <li><b>Hub:</b> installing the Pybricks firmware once is required. After that, updates are optional — HubPilot warns you if a hub is too old for Xbox pairing.</li>
        <li>Updating takes about a minute over USB and erases the setup stored on the hub (your saved projects are safe).</li>
        <li>You can always go back to LEGO firmware with the SPIKE app — LEGO's bootloader is never touched.</li>
        <li><b>Controller:</b> needs its Bluetooth LE update once, via the Xbox Accessories app on Windows or an Xbox console. <Link className="font-semibold text-primary underline" to="/firmware">Step-by-step guide.</Link></li>
      </ul>
    ),
  },
  {
    id: "browsers",
    q: "Which computers and browsers work?",
    a: <p>Chrome or Edge on Windows, macOS and ChromeOS, and Chrome on Android. Chromebooks are great for classrooms. Linux and Brave work after turning on a browser setting. Safari (Mac, iPad, iPhone) and Firefox don't support Web Bluetooth, so they can set up and save robots but not connect to hubs. <Link className="font-semibold text-primary underline" to="/compatibility#browsers">Full compatibility list.</Link></p>,
  },
  {
    id: "free",
    q: "Is HubPilot really free?",
    a: <p>Yes. Robot setup, saving online and the guides are free for every student and teacher. The Pybricks firmware your robot runs on is free to install too, and is made by the Pybricks team.</p>,
  },
];

const Hop = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-2 text-center">
    <span className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-foreground/70 bg-background"><Icon className="h-6 w-6 text-primary" /></span>
    <span className="text-xs font-semibold">{label}</span>
  </div>
);

const Line = ({ dashed }) => <span className={`h-[3px] flex-1 rounded-full ${dashed ? "border-t-[3px] border-dashed border-muted-foreground/40 bg-transparent" : "bg-primary"}`} />;

export default function HowItWorks() {
  return (
    <PageShell testId="how-it-works-page">
      <GuideHero eyebrow="Under the hood" title="How the connection works" icon={BookOpen}>
        Straight answers about pairing an Xbox controller with a SPIKE Prime hub — what's hard, how Pybricks does it, and how HubPilot keeps it reliable.
      </GuideHero>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-8 lg:py-16">
        <div className="rounded-3xl border-2 border-primary bg-card p-6 lg:p-8" data-testid="connection-overview">
          <p className="eyebrow text-primary">How HubPilot connects</p>
          <div className="mt-6 grid gap-6 md:grid-cols-2 md:gap-10">
            <div>
              <div className="flex items-center gap-3"><Hop icon={Gamepad2} label="Controller" /><Line /><Hop icon={Cpu} label="Hub" /></div>
              <p className="mt-4 text-sm text-muted-foreground">The hub pairs straight to the Xbox controller and reads its sticks and buttons while you drive.</p>
            </div>
            <div>
              <div className="flex items-center gap-3"><Hop icon={Laptop} label="Laptop" /><Line dashed /><Hop icon={Cpu} label="Hub" /></div>
              <p className="mt-4 text-sm text-muted-foreground">The laptop only sends your robot setup to the hub. After that it can be closed.</p>
            </div>
          </div>
        </div>

        <Accordion type="single" collapsible defaultValue="direct" className="mt-10 rounded-3xl border bg-card px-6" data-testid="faq-accordion">
          {FAQ.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="last:border-b-0">
              <AccordionTrigger className="text-left font-display text-lg font-bold hover:no-underline" data-testid={`faq-${f.id}-trigger`}>{f.q}</AccordionTrigger>
              <AccordionContent className="space-y-3 text-base leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </PageShell>
  );
}
